import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Aggregator dashboard — Leaflet map + markers, with Turbo Frame side panel.
// Reacts to filter changes by re-reading the JSON events list embedded in
// the page and toggling marker visibility, and fetches event details into
// the right-hand frame when a marker is clicked.
export default class extends Controller {
  static targets = ["map", "emptyDetailTemplate"]
  static values = {
    events: Array,
    bounds: { type: String, default: "amb" },
    geojsonUrl: String,
    primaryColor: { type: String, default: "#0095d9" },
    secondaryColor: { type: String, default: "#003d7a" },
    eventUrlTemplate: String
  }

  // AMB bounding box fallback (approx, in case GeoJSON fails to load)
  AMB_BOUNDS = [[41.21, 1.83], [41.55, 2.34]]
  SPAIN_VIEW = { center: [40.0, -3.7], zoom: 6 }
  WORLD_VIEW = { center: [20, 0], zoom: 2 }

  connect() {
    this.markers = new Map()       // event_id → L.marker
    this.activeMarker = null
    this.initMap()
    this.loadGeoJson()
    this.renderMarkers(this.eventsValue)
    this.boundFrameLoad = this.onFrameLoad.bind(this)
    document.addEventListener("turbo:frame-load", this.boundFrameLoad)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    if (this.map) this.map.remove()
  }

  initMap() {
    const initial = this.viewForBounds()
    this.map = L.map(this.mapTarget, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      preferCanvas: true
    }).setView(initial.center, initial.zoom)

    // Clean light base map (Carto Positron) so events sit on a real map.
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap &copy; CARTO"
    }).addTo(this.map)
  }

  viewForBounds() {
    switch (this.boundsValue) {
      case "spain": return this.SPAIN_VIEW
      case "world": return this.WORLD_VIEW
      default:      return { center: [41.38, 2.17], zoom: 11 } // Barcelona-ish
    }
  }

  async loadGeoJson() {
    if (!this.geojsonUrlValue) {
      this.fitDefaultBounds()
      return
    }
    try {
      const res = await fetch(this.geojsonUrlValue)
      if (!res.ok) throw new Error(`GeoJSON ${res.status}`)
      const geojson = await res.json()
      // Use the AMB geojson only to frame the view — we don't draw it. The base
      // tiles + markers are enough, and the approximate outline only confused.
      const layer = L.geoJSON(geojson, { interactive: false })
      try { this.map.fitBounds(layer.getBounds(), { padding: [20, 20] }) }
      catch (e) { this.fitDefaultBounds() }
    } catch (e) {
      console.warn("Aggregator: GeoJSON not available, using fallback bounds.", e)
      this.fitDefaultBounds()
    }
  }

  fitDefaultBounds() {
    if (this.boundsValue === "amb") {
      this.map.fitBounds(this.AMB_BOUNDS, { padding: [20, 20] })
    }
  }

  renderMarkers(events) {
    // Remove previous markers
    this.markers.forEach(m => this.map.removeLayer(m))
    this.markers.clear()
    this.activeMarker = null

    events.forEach(ev => {
      if (ev.lat == null || ev.lng == null) return
      const marker = L.marker([ev.lat, ev.lng], {
        icon: this.buildIcon(ev),
        riseOnHover: true
      })
      marker.on("click", () => this.onMarkerClick(ev, marker))
      marker.addTo(this.map)
      this.markers.set(ev.id, marker)
    })
  }

  buildIcon(ev) {
    const fixed = ev.kind === "fixed"
    const glyph = fixed ? this.fixedGlyph() : this.eventGlyph()
    const cls = `aggregator-pin ${fixed ? 'is-fixed' : ''}`
    return L.divIcon({
      className: "aggregator-pin-wrapper",
      html: `<div class="${cls}" data-event-id="${ev.id}" title="${this.escape(ev.name)}">${glyph}</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })
  }

  fixedGlyph() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.5c-3.5 4-6 7.2-6 10.2a6 6 0 0 0 12 0c0-3-2.5-6.2-6-10.2z"/></svg>'
  }
  eventGlyph() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/></svg>'
  }

  onMarkerClick(ev, marker) {
    this.highlight(marker)
    const url = this.eventUrlTemplateValue.replace("__ID__", ev.id)
    const frame = document.getElementById("aggregator_event_detail")
    if (frame) frame.src = url
    // Scroll the right panel to top so the detail is visible
    const panel = this.element.querySelector(".aggregator-panel")
    if (panel) panel.scrollTo({ top: 0, behavior: "smooth" })
  }

  highlight(marker) {
    if (this.activeMarker) {
      const prev = this.activeMarker.getElement()?.querySelector(".aggregator-pin")
      if (prev) prev.classList.remove("is-active")
    }
    const next = marker.getElement()?.querySelector(".aggregator-pin")
    if (next) next.classList.add("is-active")
    this.activeMarker = marker
  }

  clearSelection() {
    if (this.activeMarker) {
      const prev = this.activeMarker.getElement()?.querySelector(".aggregator-pin")
      if (prev) prev.classList.remove("is-active")
      this.activeMarker = null
    }
    const frame = document.getElementById("aggregator_event_detail")
    if (frame) {
      // Clearing src triggers a fetch to the page URL — instead, replace HTML
      // directly with the empty state we cached on initial load.
      frame.innerHTML = this.emptyDetailHtml()
    }
  }

  emptyDetailHtml() {
    if (this.hasEmptyDetailTemplateTarget) {
      return this.emptyDetailTemplateTarget.innerHTML
    }
    return ""
  }

  // When the totals frame reloads (filters), re-read embedded events JSON if
  // a fresh dataset is provided via the frame's `data-events` attribute.
  onFrameLoad(event) {
    const frame = event.target
    if (!frame || frame.id !== "aggregator_totals") return
    const fresh = frame.dataset.events
    if (!fresh) return
    try {
      const events = JSON.parse(fresh)
      this.eventsValue = events
      this.renderMarkers(events)
    } catch (e) {
      console.warn("Aggregator: could not parse refreshed events", e)
    }
  }

  escape(str) {
    return String(str || "").replace(/[<>&"]/g, c => ({"<":"&lt;",">":"&gt;","&":"&amp;","\"":"&quot;"}[c]))
  }
}
